// El panel del equipo Glubi es una aplicación aparte de la de los negocios (Fase 4). No comparte pantallas con
// ella a propósito: lo que el equipo ve, lo que puede tocar y lo que queda anotado son otra cosa, y mezclarlo
// dentro del producto del cliente es la forma más fácil de que algún día se vea lo que no se debe ver (§10).
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRootRoute, createRoute, createRouter, Navigate, Outlet, RouterProvider } from '@tanstack/react-router';
import '@glubi/ds/estilos.css';
import './estilos.css';
import { Cargando, Icon } from '@glubi/ds';
import { SesionProvider, useSesion } from './lib/sesion';
import { Marco } from './componentes/Marco';
import { Entrar } from './pantallas/Entrar';
import { Cuentas } from './pantallas/Cuentas';
import { Certificacion } from './pantallas/Certificacion';
import { Riesgos } from './pantallas/Riesgos';
import { Ingesta } from './pantallas/Ingesta';
import { Cobro } from './pantallas/Cobro';
import { Costo } from './pantallas/Costo';
import { Creditos } from './pantallas/Creditos';
import { Modelos } from './pantallas/Modelos';

const qc = new QueryClient({ defaultOptions: { queries: { staleTime: 15_000, retry: 1, refetchOnWindowFocus: true } } });

// Quién entra no lo decide esta aplicación sino la base: glubi.personal_glubi. Un correo de cliente inicia
// sesión en Supabase igual que siempre, y acá se queda en la puerta.
function Protegida() {
  const { sesion, listo, yo, toast } = useSesion();
  if (!listo) return <Cargando />;
  if (!sesion) return <Entrar />;
  if (!yo) return <Cargando texto="Abriendo el panel" />;
  if (!yo.personal) {
    return <Entrar aviso={`${yo.usuario.correo ?? 'Esa cuenta'} no es del equipo de Glubi. Los negocios entran por la aplicación, no por el panel.`} />;
  }
  return (
    <>
      <Marco><Outlet /></Marco>
      {toast && <div className="toast" role="status" key={toast.id}><Icon name="check" size={16} color="var(--amber)" />{toast.texto}</div>}
    </>
  );
}

const raiz = createRootRoute({ component: () => <Outlet /> });
const app = createRoute({ getParentRoute: () => raiz, id: 'panel', component: Protegida });
const pantalla = (path: string, component: () => React.ReactNode) => createRoute({ getParentRoute: () => app, path, component });

const arbol = raiz.addChildren([
  app.addChildren([
    pantalla('/', () => <Navigate to="/cuentas" />),
    pantalla('/cuentas', Cuentas),
    pantalla('/riesgos', Riesgos),
    pantalla('/ingesta', Ingesta),
    pantalla('/certificacion', Certificacion),
    pantalla('/cobro', Cobro),
    pantalla('/costo', Costo),
    pantalla('/creditos', Creditos),
    pantalla('/modelos', Modelos),
  ]),
]);

const router = createRouter({ routeTree: arbol, defaultPreload: 'intent' });

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={qc}>
      <SesionProvider>
        <RouterProvider router={router} />
      </SesionProvider>
    </QueryClientProvider>
  </StrictMode>,
);
